/**
 * net/index.js — the social layer: hub presence, neighbour-lane replay,
 * personal-space visits, and the ambient geese (GDD 10).
 *
 * Replaces the stub of the same name and keeps its interface exactly:
 * `presence`, `replay`, `isEnabled`. Everything else is additive.
 *
 * ── Two independent halves ──
 * The NPC flock and the PeerJS layer are deliberately unrelated. NPCs always
 * run — offline, online, and in test mode — because the hub has to feel
 * inhabited on a first load with no broker and no other players. Real
 * networking is strictly best-effort on top of that: it either works and adds
 * live geese, or it quietly does not and nobody sees an error.
 *
 * ── Self-installation ──
 * This module boots itself off `WORLD_READY` rather than asking `main.js` for a
 * wiring line. `main.js` already imports it, so the subscription is live long
 * before that event fires, and the whole social layer becomes a module another
 * agent's file never has to mention.
 *
 * @module net
 */

import { EVENTS } from '../constants.js';
import { bus } from '../core/events.js';
import { options } from '../core/testmode.js';
import { presence } from './presence.js';
import { npcGeese } from './npc-geese.js';
import { replaySystem, arcPosition, laneAnchor as laneAnchorVec } from './replay.js';
import { visits, visitTest } from './visits.js';
import { badge as badgeUi } from './badge.js';
import { RateLimiter, roundTrip } from './protocol.js';
import { peerManager } from './peer-manager.js';

export { presence } from './presence.js';
export { npcGeese } from './npc-geese.js';
export { visits, visitTest } from './visits.js';
export { peerManager } from './peer-manager.js';
export { replaySystem, arcPosition } from './replay.js';
export * as protocol from './protocol.js';

/**
 * Whether real networking is available at all.
 *
 * False under `?test=1`, unconditionally — determinism and a shared public
 * broker cannot both be true.
 * @returns {boolean}
 */
export function isEnabled() {
  return !options.test && typeof window !== 'undefined';
}

// ─────────────────────────────────────────────────────────────────────────────
// The `replay` stub surface, kept
//
// The stub described a record-then-play-a-clip model. The shipped design
// broadcasts each throw as it happens and the receiver evaluates the arc, which
// needs no buffering and no clock sync. The clip methods are still here and
// still honest: they collect the round's throws, which is what a future
// spectator mode (GDD 10.5, Post-Launch 2) would want.
// ─────────────────────────────────────────────────────────────────────────────

/** @type {{ stallId: string, throws: object[], startedAt: number }|null} */
let recording = null;

export const replay = {
  /** @param {string} stallId */
  startRecording(stallId) {
    recording = { stallId, throws: [], startedAt: performance.now() };
  },

  /** @returns {object|null} The clip, or null if nothing was recorded. */
  stopRecording() {
    const clip = recording;
    recording = null;
    return clip && clip.throws.length > 0 ? clip : null;
  },

  /**
   * Record one throw into the open clip.
   * @param {{ velocity:number[], position:number[], t?:number }} data
   */
  recordThrow(data) {
    if (!recording) return;
    recording.throws.push({ ...data, t: data.t ?? performance.now() - recording.startedAt });
  },

  /**
   * Render a throw on a neighbouring lane.
   * @param {object} clip A single throw, or a clip whose first throw is used.
   * @param {number} laneIndex
   * @returns {boolean}
   */
  play(clip, laneIndex = 0) {
    if (!clip) return false;
    const shot = Array.isArray(clip.throws) ? clip.throws[0] : clip;
    if (!shot) return false;
    return replaySystem.spawn({
      stallId: shot.stallId || clip.stallId,
      origin: shot.origin || shot.position,
      velocity: shot.velocity,
      gravityScale: shot.gravityScale,
      kind: shot.kind,
      laneIndex,
    });
  },

  clear() {
    replaySystem.clear();
  },

  /** @returns {object} Live replay state — what is on screen right now. */
  state() {
    return replaySystem.state();
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Boot
// ─────────────────────────────────────────────────────────────────────────────

let started = false;

/**
 * Build the social layer into a live scene. Idempotent.
 * @param {import('three').Scene} scene
 * @param {import('../core/engine.js').engine} engine
 */
export function install(scene, engine) {
  if (started || !scene || !engine) return;
  started = true;

  npcGeese.install(scene);
  replaySystem.install(scene);
  presence.install(scene, { npcCount: () => npcGeese.geese.length });

  engine.onUpdate((dt) => update(dt));
  installTestHooks();

  if (!isEnabled()) return;

  // Deferred so the connection attempt never competes with the first frames.
  // Failure here is silent by design: the badge already says "offline".
  const room = new URLSearchParams(location.search).get('room');
  setTimeout(() => {
    presence.connect(room).catch(() => {});
  }, 1200);
}

/**
 * One frame of the social layer.
 * @param {number} dt
 */
export function update(dt) {
  npcGeese.update(dt);
  presence.update(dt);
  replaySystem.update(dt);
}

// WORLD_READY fires once, after the world is built, and before the `?stall=`
// deep link — so presence's STALL_ACTIVATE subscription beats any activation.
// ENGINE_READY is emitted twice and its first emission lands before the world
// exists; installing on that was correct only by accident of ordering, since
// nothing here reads a hub object (NPC waypoints come from the same constants
// hub.js builds from). See ARCHITECTURE §3.
bus.on(EVENTS.WORLD_READY, ({ engine, scene } = {}) => install(scene, engine));

// ─────────────────────────────────────────────────────────────────────────────
// Automation surface
//
// `testmode.js` routes `__cc.net.*` and `__cc.npc.*` here. Everything returns
// plain, structured-cloneable data because it crosses the Playwright boundary —
// which is also why the THREE-typed forms of these live in their own modules
// and only the serialising wrappers are exported from the index.
// ─────────────────────────────────────────────────────────────────────────────

/** Pack then unpack a message of any of the nine types. @returns {object} */
export function protocolTest(spec) {
  return roundTrip(spec);
}

/** Ballistic position at time `t`, as a plain array. @returns {number[]} */
export function arcAt(origin, velocity, gravityScale, t) {
  return arcPosition(origin, velocity, gravityScale, t).toArray();
}

/**
 * A neighbour lane's world anchor, serialised. The `THREE.Vector3` form is
 * `laneAnchor` in `replay.js`; this is the one that survives `page.evaluate`.
 * @returns {{position:number[], yaw:number, offset:number[]}|null}
 */
export function laneAnchor(stallId, laneIndex) {
  const a = laneAnchorVec(stallId, laneIndex);
  return a ? { position: a.position.toArray(), yaw: a.yaw, offset: a.offset.toArray() } : null;
}

/** Render a cosmetic replay arc with no peer involved. @returns {boolean} */
export function spawnReplay(data) {
  return replaySystem.spawn(data);
}

/** @returns {object} Live replay state, including the always-zero body count. */
export function replayState() {
  return replaySystem.state();
}

/**
 * Feed a raw wire message in as though a peer had sent it — through the same
 * version check, sanitisers and routing a real one goes through. This is what
 * lets remote geese, lane seating and replay be tested with no network at all.
 * @param {string} peerId
 * @param {object} message
 */
export function simulateInbound(peerId, message) {
  return presence._receive(message, peerId);
}

/** @returns {object} */
export function presenceState() {
  return presence.state();
}

/** @returns {object} What the bottom-right badge is showing. */
export function badge() {
  return badgeUi.snapshot();
}

/** @returns {object} */
export function visitState() {
  return visits.state();
}

/** @returns {object[]} */
export function peers() {
  return presence.peers();
}

/** @returns {string|null} */
export function room() {
  return presence.room;
}

/** @returns {boolean} */
export function disconnect() {
  presence.disconnect();
  return true;
}

/** @returns {object} Transport status: connected, room id, host flag. */
export function transport() {
  return peerManager.status();
}

/**
 * Drive the inbound flood guard with `count` messages inside one second and
 * report how many got through. Uses a fresh limiter and an injected clock, so
 * it asserts the policy without needing a peer that actually floods us.
 * @param {number} [count]
 * @returns {{ sent:number, allowed:number, dropped:number, cap:number }}
 */
export function rateLimitTest(count = 60) {
  const limiter = new RateLimiter();
  const now = 1_000_000;
  let allowed = 0;
  for (let i = 0; i < count; i++) {
    if (limiter.allow('flooder', now)) allowed++;
  }
  return { sent: count, allowed, dropped: limiter.dropped, cap: limiter.perSecond };
}

/**
 * Attach `__cc.net.*` / `__cc.npc.*` directly, for any key `testmode.js` does
 * not already define. Eight agents edit that file in parallel; a social layer
 * that silently loses its test hooks to somebody else's merge is a worse
 * outcome than a few lines of belt and braces.
 */
function installTestHooks() {
  const cc = typeof window !== 'undefined' ? window.__cc : null;
  if (!cc) return;

  const netHooks = {
    protocolTest, arcAt, laneAnchor, spawnReplay, replayState, simulateInbound,
    presenceState, visitTest, visitState, peers, room, disconnect, rateLimitTest,
    badge,
    lobbies: transport,
  };

  cc.net = cc.net || {};
  for (const [key, fn] of Object.entries(netHooks)) {
    if (!cc.net[key]) cc.net[key] = fn;
  }

  cc.npc = cc.npc || {
    list: () => npcGeese.list(),
    state: () => npcGeese.state(),
    honk: (i) => npcGeese.honk(i),
  };
}

export default { presence, replay, isEnabled, npcGeese, visits, install, update };
